import { Inject, Injectable, DOCUMENT } from '@angular/core';

import { Observable, ReplaySubject, concatMap, delay, first, of, tap } from 'rxjs';

import {
  AppRouter,
  MenuService,
  ROUTER,
  SecurityService,
  ModalNotificationLauncherService,
  MessagesService,
  ModalTriggerConfiguration, IfFeatureService
} from '@tdwb/core';

import {
  OrderNotificationsToastService
} from './modules/trading/order-notifications-toast/order-notifications-toast.service';

/**
 * Configures navigation and modals displayed at startup
 */
@Injectable({
  providedIn: 'root'
})
export class WebbrokerInitNavigationService {
  private readonly CRITICAL_MESSAGE_ROUTE_NAME: string = 'modal.messages';
  private readonly MODAL_NOTIFICATION_ROUTE_NAME: string = 'modal.modal-notification';
  private readonly AGREEMENTS_MODAL_ROUTE_NAME: string = 'modal.unsigned-agreements';

  constructor(@Inject(DOCUMENT) protected document: Document,
              @Inject(ROUTER) protected appRouter: AppRouter,
              protected securityService: SecurityService,
              protected menuService: MenuService,
              protected messagesService: MessagesService,
              protected featureService: IfFeatureService,
              protected modalNotificationLauncherService: ModalNotificationLauncherService,
              protected orderNotificationsToastService: OrderNotificationsToastService
  ) {}

  init(): void {
    if (this.securityService.hasPermission('NEW_USER')) {
      this.menuService.disable();
      this.menuService.hideFooter();
    } else {
      this.initializeModals();
    }
    this.appRouter.handleFocusOnPageTransition();
  }

  /**
   * Modal sequence: W-8BEN > Critial Message > Home Page What's New > Other Pages What's New
   * To pop up a new modal, need to close the previous modal first
   * Have a delay before all modals to avoid transition supersede issue, TDWARTSE-6240
   */
  private initializeModals(): void {
    this.appRouter.registerPageInitialLoad(() => {
      of(true)
        .pipe(
          delay(0),
          concatMap(() => this.initAgreements()),
          concatMap(() => this.initCriticalMessages()),
          concatMap(() => this.initHomePageModalNotification())
        )
        .subscribe(() => {
          this.initPagesModalNotification();
        });
    });
  }

  private initAgreements(): Observable<boolean> {
    const agreements$ = new ReplaySubject<boolean>();
    const allAgreementsSigned = this.securityService.hasPermission('ALL_AGREEMENTS_SIGNED') ?? false;
    const mandatoryAgreements = this.featureService.getFeatureFlag<boolean>('mandatoryAgreementsWBR') ?? false;

    if(mandatoryAgreements && !allAgreementsSigned) {
      this.appRouter.registerModalClose(this.AGREEMENTS_MODAL_ROUTE_NAME, () => {
        agreements$.next(true);
        agreements$.complete();
      });
      this.appRouter.navigate(this.AGREEMENTS_MODAL_ROUTE_NAME);
    } else {
      agreements$.next(true);
      agreements$.complete();
    }

    return agreements$;
  }

  private initCriticalMessages(): Observable<boolean> {
    const criticalMessages$ = new ReplaySubject<boolean>;

    this.messagesService
      .retrieveStartupMessages()
      .pipe(
        first(),
        tap((): void => this.orderNotificationsToastService.init())
      )
      .subscribe((hasCriticalMessage: boolean): void => {
        if (hasCriticalMessage) {
          this.appRouter.registerModalClose(this.CRITICAL_MESSAGE_ROUTE_NAME, () => {
            criticalMessages$.next(true);
            criticalMessages$.complete();
          });
          this.appRouter.navigate(this.CRITICAL_MESSAGE_ROUTE_NAME);
        } else {
          criticalMessages$.next(true);
          criticalMessages$.complete();
        }
      });

    return criticalMessages$;
  }

  private initHomePageModalNotification(): Observable<boolean> {
    const modalNotification$ = new ReplaySubject<boolean>();
    const triggerConfig = this.modalNotificationLauncherService.getMatchedModalNotificationTrigger();
    const params = this.getModalNotificationTransitionParams(triggerConfig);

    if (params) {
      this.appRouter.registerModalClose(this.MODAL_NOTIFICATION_ROUTE_NAME, () => {
        modalNotification$.next(true);
        modalNotification$.complete();
      });
      this.appRouter
        .navigate(this.MODAL_NOTIFICATION_ROUTE_NAME, params)
        .then(() => this.modalNotificationLauncherService.registerTriggerLaunch(triggerConfig));
    } else {
      modalNotification$.next(true);
      modalNotification$.complete();
    }

    return modalNotification$;
  }

  private initPagesModalNotification(): void {
    this.appRouter.registerPagesLoad(() => {
      const triggerConfig = this.modalNotificationLauncherService.getMatchedModalNotificationTrigger();
      const params = this.getModalNotificationTransitionParams(triggerConfig);

      if (params) {
        this.appRouter
          .navigate(this.MODAL_NOTIFICATION_ROUTE_NAME, params)
          .then(() => this.modalNotificationLauncherService.registerTriggerLaunch(triggerConfig));
      }
    });
  }

  private getModalNotificationTransitionParams(triggerConfig: ModalTriggerConfiguration): Record<string, unknown> {
    return triggerConfig
      ? {
        triggerId: triggerConfig.id,
        triggerLinkId: triggerConfig.triggerLinkId,
        persistence: triggerConfig.persistence
      }
      : null;
  }
}
